import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Screen } from '@/components/Screen'
import { Kicker } from '@/components/Kicker'
import { Button } from '@/components/Button'
import { ConfidenceChip } from '@/components/ConfidenceChip'
import { ListRow } from '@/components/ListRow'
import { PlusIcon, NeedIcon } from '@/design-system/icons'
import { SkeletonList } from '@/components/Skeleton'
import { useAppStore } from '@/stores/appStore'
import { supabase } from '@/lib/supabase'
import { PACK_COLORS, packForNeedCategory } from '@/design-system/tokens'
import { IconForPack } from '@/design-system/icons'
import type { Need, NeedStatus, NeedUrgency } from '@/lib/types'
import { relativeTime } from '@/lib/format'
import { recurrenceLabel } from '@/lib/needPresets'

const URGENCY_ORDER: Record<NeedUrgency, number> = { critical: 0, high: 1, normal: 2, low: 3 }

type FilterStatus = 'open_and_progress' | 'open' | 'in_progress' | 'resolved' | 'all'

export function NeedsListScreen() {
  const nav = useNavigate()
  const familyId = useAppStore((s) => s.familyId)
  const [filter, setFilter] = useState<FilterStatus>('open_and_progress')

  const query = useQuery({
    enabled: !!familyId,
    queryKey: ['needs', familyId, filter],
    queryFn: async () => {
      let q = supabase.from('needs').select('*').eq('family_id', familyId!)
      if (filter === 'open') q = q.eq('status', 'open')
      else if (filter === 'in_progress') q = q.eq('status', 'in_progress')
      else if (filter === 'resolved') q = q.eq('status', 'resolved')
      else if (filter === 'open_and_progress') q = q.in('status', ['open', 'in_progress'])
      const { data, error } = await q.order('urgency', { ascending: true }).order('created_at', { ascending: false }).limit(200)
      if (error) throw error
      return (data ?? []) as Need[]
    },
  })

  const grouped = useMemo(() => {
    const rows = query.data ?? []
    const sorted = [...rows].sort((a, b) => {
      const u = URGENCY_ORDER[a.urgency] - URGENCY_ORDER[b.urgency]
      if (u !== 0) return u
      return b.created_at.localeCompare(a.created_at)
    })
    const buckets: Record<NeedUrgency, Need[]> = { critical: [], high: [], normal: [], low: [] }
    for (const n of sorted) buckets[n.urgency].push(n)
    return buckets
  }, [query.data])

  return (
    <Screen
      title="Needs"
      action={
        <button
          type="button"
          onClick={() => nav('/needs/new')}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full text-text-secondary hover:bg-white/5"
          aria-label="New need"
        >
          <PlusIcon size={20} />
        </button>
      }
    >
      <div className="px-4 py-3 space-y-4">
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          <FilterChip label="Active" active={filter === 'open_and_progress'} onClick={() => setFilter('open_and_progress')} />
          <FilterChip label="Open" active={filter === 'open'} onClick={() => setFilter('open')} />
          <FilterChip label="In progress" active={filter === 'in_progress'} onClick={() => setFilter('in_progress')} />
          <FilterChip label="Resolved" active={filter === 'resolved'} onClick={() => setFilter('resolved')} />
          <FilterChip label="All" active={filter === 'all'} onClick={() => setFilter('all')} />
        </div>

        {query.isLoading && <Loading />}
        {query.isError && <ErrorPanel message={(query.error as Error).message} />}

        {query.data && query.data.length === 0 && <EmptyPanel filter={filter} />}

        {(['critical', 'high', 'normal', 'low'] as NeedUrgency[]).map((u) => {
          const rows = grouped[u]
          if (rows.length === 0) return null
          return (
            <section key={u}>
              <Kicker color={urgencyColor(u)}>{u === 'critical' ? 'Critical' : `${u} priority`}</Kicker>
              <div className="mt-2 rounded-r-sm bg-bg-elevated border border-divider-subtle divide-y divide-divider-subtle">
                {rows.map((n) => (
                  <NeedRow key={n.id} need={n} onClick={() => nav(`/needs/${n.id}`)} />
                ))}
              </div>
            </section>
          )
        })}
      </div>
    </Screen>
  )
}

function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="whitespace-nowrap rounded-full px-3 py-1.5 text-caption font-medium border transition-colors"
      style={
        active
          ? { background: '#F4EFE7', color: '#0B0F0E', borderColor: '#F4EFE7' }
          : { background: 'transparent', color: '#C4CBC6', borderColor: 'rgba(255,255,255,0.16)' }
      }
    >
      {label}
    </button>
  )
}

function NeedRow({ need, onClick }: { need: Need; onClick: () => void }) {
  const pack = packForNeedCategory(need.category)
  const color = PACK_COLORS[pack].light
  const Icon = IconForPack[pack]
  const subtitleParts: string[] = []
  if (need.due_at) subtitleParts.push(`due ${relativeTime(need.due_at)}`)
  if (need.recurrence) subtitleParts.push(`↻ ${recurrenceLabel(need.recurrence)}`)
  subtitleParts.push(`from ${need.source_type}`)
  subtitleParts.push(need.status)
  return (
    <ListRow
      icon={<Icon size={18} />}
      iconColor={color}
      title={need.title}
      subtitle={subtitleParts.join(' · ')}
      trailing={<ConfidenceChip confidence={need.confidence} showLabel={false} />}
      onClick={onClick}
    />
  )
}

function EmptyPanel({ filter }: { filter: FilterStatus }) {
  const nav = useNavigate()
  const isActiveFilter = filter === 'open_and_progress' || filter === 'all'
  return (
    <div className="rounded-r-sm border border-divider-subtle bg-bg-elevated py-12 px-6 text-center">
      <NeedIcon size={32} className="mx-auto text-text-quiet" />
      <div className="mt-4 font-serif italic text-title text-text-secondary">
        {filter === 'resolved' ? 'Nothing resolved yet.' : filter === 'open' ? 'No open needs.' : 'All clear.'}
      </div>
      <div className="mt-2 text-caption text-text-tertiary max-w-xs mx-auto">
        {isActiveFilter
          ? "Needs are things that require your attention — pediatric appointments, running low on formula, permission slips. Log them here so nothing slips."
          : 'No needs match this filter yet.'}
      </div>
      {isActiveFilter && (
        <div className="mt-5">
          <Button variant="secondary" size="md" onClick={() => nav('/needs/new')}>
            Add a need
          </Button>
        </div>
      )}
    </div>
  )
}

function Loading() {
  return <div className="pt-3"><SkeletonList rows={4} /></div>
}
function ErrorPanel({ message }: { message: string }) {
  return (
    <div className="rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 p-3 text-caption text-pack-safety">
      {message}
    </div>
  )
}

function urgencyColor(u: NeedUrgency): string {
  if (u === 'critical') return PACK_COLORS.safety.light
  if (u === 'high') return PACK_COLORS.needs.light
  if (u === 'normal') return PACK_COLORS.health.light
  return PACK_COLORS.teen.light
}
export function statusColor(s: NeedStatus): string {
  if (s === 'resolved') return PACK_COLORS.early_care.light
  if (s === 'in_progress') return PACK_COLORS.needs.light
  return PACK_COLORS.health.light
}
